import { Button } from "@/components/ui/button";
import { API_URL } from "@/config";
import {
  ScannerStrategyOption,
  usePatternUniverses,
} from "@/contexts/PatternUniverse";
import { Eye } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import ViewModal from "../StrategyViewModal";

import { DeployScannerModal } from "@/components/run-scan-page/DeployScannerModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Separator } from "@/components/ui/separator";
import { Pagination, randomString } from "@/helpers/helperFunctions";
import { AlertDialogDescription } from "@radix-ui/react-alert-dialog";
import axios from "axios";
import { useRouter } from "next/router";
import { BiEdit } from "react-icons/bi";
import { FaCube } from "react-icons/fa6";
import { MdClear } from "react-icons/md";
import { useUserContext } from "@/contexts/userContext";
import EditNameModal from "@/components/common/EditNameModal";

type DeployScannerData = {
  scanner_id: string;
  scanner_name: string;
  isDeploying: boolean;
};

const PAGE_SIZE = 5;

const UserScanners: React.FC = () => {
  const { refetchScannerStrategies, scannerStrategies } = usePatternUniverses();
  const router = useRouter();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [selected, setSelected] = useState<any>({});
  const [deployScannerData, setDeployScannerData] =
    useState<DeployScannerData | null>(null);
  const [page, setPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const { user } = useUserContext();

  const handleScannerStrategyView = (scanner: ScannerStrategyOption) => {
    const canvasData = scanner.canvas_data
      ? JSON.parse(scanner.canvas_data)
      : {};
    setSelected({
      nodes: canvasData?.filter?.((item: any) => item.type === "selectorNode"),
      edges: canvasData?.filter?.((item: any) => item.type === "buttonedge"),
      symbol: scanner.symbol,
      timeframe: scanner.timeFrame,
    });
    setShowModal(true);
  };

  const handleStrategyDelete = async (scanner: ScannerStrategyOption) => {
    const url = new URL("delete_market_scanner_strategy", API_URL);
    url.searchParams.append("user_id", user?.user_id ?? "");
    url.searchParams.append("strategy_key", scanner.keygen);
    const res = await axios.post(url.toString());
    if (res.status === 200) {
      refetchScannerStrategies(true, user?.user_id ?? "");
    }
  };

  const handleDeployScanner = useCallback(
    (scanner: ScannerStrategyOption) => {
      setDeployScannerData({
        scanner_id: scanner.strategy_keygen,
        scanner_name: scanner.label,
        isDeploying: true,
      });
    },
    [scannerStrategies]
  );
  const handleRunScan = useCallback(
    (scanner: ScannerStrategyOption) => {
      router.push({
        pathname: "/run-scan",
        query: {
          locale: router.query.locale || "en",
          keygen: [scanner.strategy_keygen],
          scanner: scanner.label,
        },
      });
    },
    [scannerStrategies]
  );

  const handleEditStrategy = useCallback((scanner: ScannerStrategyOption) => {
    const userInfo = JSON.parse(localStorage.getItem("user-info")!);
    router.push(
      "/edit-strategy/" +
        encodeURIComponent(userInfo?.user_id) +
        "/" +
        scanner.value +
        randomString(5) +
        "/" +
        userInfo.token +
        "/false" +
        `?ref=${router.asPath}&locale=${router.query.locale || "en"}`
    );
  }, []);

  useEffect(() => {
    refetchScannerStrategies(
      true,
      user?.user_id ?? "",
      page,
      PAGE_SIZE,
      setTotalCount
    );
  }, [page]);

  return (
    <div className="flex flex-col items-start gap-4 w-full py-8 px-4">
      <div className="flex flex-col w-full gap-2 min-h-64">
        {scannerStrategies?.map((scanner, ind) => {
          return (
            <div className="flex flex-col w-full gap-2" key={ind}>
              <div className="flex flex-row w-full justify-between items-center gap-8 ">
                <div className="flex">
                  <p className="font-normal">{scanner.label}</p>
                  <EditNameModal strategyData={scanner}></EditNameModal>
                </div>
                <div className="flex flex-row gap-8 justify-center md:justify-end items-center">
                  <Button
                    variant="ghost"
                    onClick={() => handleScannerStrategyView(scanner)}
                  >
                    <div className="flex text-muted-foreground w-full items-center gap-2">
                      <Eye />
                      <p className="text-xs">View</p>
                    </div>
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleRunScan(scanner)}
                  >
                    <span className="text-xs">Run Scan</span>
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleDeployScanner(scanner)}
                    disabled={deployScannerData?.isDeploying}
                  >
                    <div className="flex w-full items-center gap-2">
                      <FaCube color="var(--marketplace-blue)" />
                      <p className="text-xs">Deploy</p>
                    </div>
                  </Button>

                  <Button
                    variant="secondary"
                    onClick={() => handleEditStrategy(scanner)}
                  >
                    <div className="flex w-full items-center gap-2">
                      <p className="text-xs">Edit scanner</p>
                      <BiEdit />
                    </div>
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        className="h-auto text-red-foreground hover:text-red-foreground"
                      >
                        <div className="w-full ">
                          <MdClear />
                        </div>
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {"Are you sure you want to delete this scanner?"}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {
                            "This action cannot be undone, and all associated settings will be permanently removed."
                          }
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <div className="flex items-center justify-end space-x-2 w-full">
                        <AlertDialogCancel>{"Cancel"}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            handleStrategyDelete(scanner);
                          }}
                        >
                          {"Yes"}
                        </AlertDialogAction>
                      </div>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <Pagination
        totalCount={totalCount}
        currentPage={page}
        changePage={setPage}
        limit={PAGE_SIZE}
      />
      <ViewModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        selected={selected}
      />

      {deployScannerData != null && (
        <DeployScannerModal
          open={deployScannerData?.isDeploying}
          setOpen={(open: boolean) =>
            setDeployScannerData({
              ...deployScannerData,
              isDeploying: open,
            })
          }
          scannerName={deployScannerData?.scanner_name}
          scannerId={deployScannerData?.scanner_id}
          userInfo={user}
        />
      )}
    </div>
  );
};

export default UserScanners;
